import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Header from "common/components/Header/Header";
import { TableCellsIcon } from "@heroicons/react/24/solid";
import dayjs from "dayjs";
import FormControl from "common/components/FormControl/FormControl";
import { Form } from "react-final-form";
import Button from "common/components/Button/Button";
import usePark from "common/hooks/usePark";
import useItem from "common/hooks/useItem";
import AlertsContext from "common/providers/alerts";
import AnimalSelect from "components/select/AnimalSelect/AnimalSelect";
import SpecySelect from "components/select/SpecySelect/SpecySelect";
import EnclosureSelect from "components/select/EnclosureSelect/EnclosureSelect";
import TagSelect from "components/select/TagSelect/TagSelect";
import ObservationGroupSelect from "components/select/ObservationGroupSelect/ObservationGroupSelect";
import SectorSelect from "components/select/SectorSelect/SectorSelect";
import generatePDF from "common/utils/generatePdf";
import useAuth from "common/hooks/useAuth";
import UserSelect from "components/select/UserSelect/UserSelect";
import SuperFamilySelect from "components/select/SuperFamily/SuperFamilySelect";
import ManagerSelect from "components/select/UserSelect/ManagerSelect";
import ObservationGridSelect from "components/select/ObservationGridSelect/ObservationGridSelect";
import {
  BalanceSheetDisplayOptions,
  SavedBalanceSheet,
} from "types/SavedBalanceSheet";
import useBalanceSheetData, {
  buildBalanceSheetFilters,
} from "./useBalanceSheetData";
import BalanceSheetReport from "./components/BalanceSheetReport";
import BalanceSheetDisplayToggles, {
  DEFAULT_DISPLAY_OPTIONS,
} from "./components/BalanceSheetDisplayToggles";
import SaveBalanceSheetModal from "./components/SaveBalanceSheetModal";

const styles = {
  inputContainerStyle:
    "bg-white rounded-lg border border-slate-300 xl:min-w-[280px]",
};

function BalanceSheets() {
  const { t } = useTranslation();
  const { park } = usePark();
  const { user: currentUser } = useAuth();
  const { setAlert } = useContext(AlertsContext);

  const [empty, setEmpty] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [isSendingMail, setIsSendingMail] = useState(false);
  const [displayOptions, setDisplayOptions] =
    useState<BalanceSheetDisplayOptions>(DEFAULT_DISPLAY_OPTIONS);

  const { balance, isFetching, fetchBalance } = useBalanceSheetData();

  const [isSaveModalVisible, setIsSaveModalVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { saveItem } = useItem<SavedBalanceSheet>(
    "saved-balance-sheets/data/list",
    ""
  );

  const [filters, setFilters] = useState({
    park: park?.id,
    startDate: dayjs().subtract(1, "year"),
    endDate: dayjs(),
    animal: null,
    specy: null,
    observationGroup: null,
    observationGrid: null,
    enclosure: null,
    sector: null,
    tag: null,
  });

  const cantDownloadBalanceSheets =
    (currentUser?.isAdmin ||
      currentUser?.isSuperadmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.["balance-sheet"]?.download)) ??
    false;

  const onChangeFilter = (key: any, val: any) => {
    setFilters((f) => {
      const newF = { ...f };
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      newF[key] = val;
      return newF;
    });
  };

  // init
  useEffect(() => {
    if (empty) {
      fetchBalance(filters);
      setEmpty(false);
    }
  }, [fetchBalance, empty, setEmpty, filters]);

  // Export Bilan to PDF
  const chartRef = useRef<HTMLDivElement>(null);

  const exportToPDF = async () => {
    setIsExporting(true);
    const result = await generatePDF({ chartRef });
    if (result && result.blob) {
      const { blob, filename } = result;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
    setIsExporting(false);
  };

  const sendEmailWithPDF = async () => {
    setIsSendingMail(true);
    const result = await generatePDF({ chartRef });
    if (result && result.blob) {
      const { blob, filename } = result;

      // On vient télécharger le fichier
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Création d'un email pré-rempli
      // À modifier selon le besoin
      const subject = encodeURIComponent(t("balance.mailObject"));
      const body = encodeURIComponent(
        t("balance.mailContent", {
          startDate: dayjs(balance?.startDate).format("LL"),
          endDate: dayjs(balance?.endDate).format("LL"),
        })
      );

      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    }
    setIsSendingMail(false);
  };

  const handleSave = async ({
    name,
    isFrozen,
  }: {
    name: string;
    isFrozen: boolean;
  }) => {
    setIsSaving(true);
    try {
      await saveItem({
        name,
        filters: buildBalanceSheetFilters(filters),
        displayOptions,
        isFrozen,
        snapshot: isFrozen ? balance : null,
      });
      setIsSaveModalVisible(false);
      setAlert(t("balance.saved"), "success");
    } catch (e) {
      setAlert(e, "danger");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-primary flex flex-col gap-6">
      <Header
        title={t("titlePages.balance-sheets")}
        Icon={TableCellsIcon}
        isBeforeFilters
      />

      <div className="flex justify-between mt-2 mb-0 ">
        <h2 className="text-2xl font-bold">{t("balance.mainTitle")}</h2>
        <div className="flex flex-row gap-4">
          <Button
            title={t("actions.sendByMail")}
            type="primary-line"
            icon="IoPaperPlaneOutline"
            onClick={sendEmailWithPDF}
            loading={isSendingMail}
            disabled={isSendingMail || isFetching || !cantDownloadBalanceSheets}
          />
          <Button
            title={t("actions.exportPdf")}
            type="neutral-line"
            icon="AiOutlineDownload"
            onClick={exportToPDF}
            loading={isExporting}
            disabled={isExporting || isFetching || !cantDownloadBalanceSheets}
          />
        </div>
      </div>
      <div className="rounded-2xl bg-slate-200/70 p-8 flex flex-col gap-4">
        <div className="flex flex-row gap-4 items-center justify-between">
          <h3 className="text-xl font-semibold">
            {t("balance.generateTitle")}
          </h3>
          <div className="flex flex-row gap-4 self-center">
            <Button
              onClick={() => fetchBalance(filters)}
              title={t("balance.generate")}
              type="secondary"
              className="h-[44px]"
              disabled={isFetching || isExporting}
              loading={isFetching}
            />
            <Button
              onClick={() => setIsSaveModalVisible(true)}
              title={t("balance.save")}
              type="primary-line"
              icon="RiInboxArchiveLine"
              className="h-[44px]"
              disabled={isFetching || isExporting || !balance}
            />
          </div>
        </div>

        <div className="grid grid-cols-3 xl:grid-cols-4 items-end gap-6">
          <Form
            onSubmit={() => {}}
            initialValues={{ ...filters }}
            render={() => {
              return (
                <>
                  <FormControl
                    name="startDate"
                    title={t("forms.startDate")}
                    type="date"
                    showTimeSelect={false}
                    showYearDropdown
                    required
                    inputContainerStyle={styles.inputContainerStyle}
                    onChange={(val) => {
                      onChangeFilter("startDate", val);
                    }}
                    locale={currentUser?.language ?? "fr"}
                  />

                  <FormControl
                    name="endDate"
                    title={t("forms.endDate")}
                    type="date"
                    showTimeSelect={false}
                    showYearDropdown
                    required
                    inputContainerStyle={styles.inputContainerStyle}
                    onChange={(val) => {
                      onChangeFilter("endDate", val);
                    }}
                    locale={currentUser?.language ?? "fr"}
                  />

                  <FormControl
                    title={t("forms.animal")}
                    type="custom"
                    isSelect
                    name="animal"
                    inputContainerStyle={styles.inputContainerStyle}
                    onChange={(val) => {
                      onChangeFilter("animal", val);
                    }}
                  >
                    <AnimalSelect />
                  </FormControl>

                  <FormControl
                    title={t("forms.specy")}
                    type="custom"
                    isSelect
                    name="specy"
                    inputContainerStyle={styles.inputContainerStyle}
                    onChange={(val) => {
                      onChangeFilter("specy", val);
                    }}
                  >
                    <SpecySelect />
                  </FormControl>

                  <FormControl
                    title={t("forms.observationGroup")}
                    type="custom"
                    isSelect
                    name="observationGroup"
                    inputContainerStyle={styles.inputContainerStyle}
                    onChange={(val) => {
                      onChangeFilter("observationGroup", val);
                    }}
                  >
                    <ObservationGroupSelect />
                  </FormControl>

                  <FormControl
                    title={t("forms.observationGrid")}
                    type="custom"
                    isSelect
                    name="observationGrid"
                    inputContainerStyle={styles.inputContainerStyle}
                    onChange={(val) => {
                      onChangeFilter("observationGrid", val);
                    }}
                  >
                    <ObservationGridSelect />
                  </FormControl>

                  <FormControl
                    title={t("forms.enclosure")}
                    type="custom"
                    isSelect
                    name="enclosure"
                    inputContainerStyle={styles.inputContainerStyle}
                    onChange={(val) => {
                      onChangeFilter("enclosure", val);
                    }}
                  >
                    <EnclosureSelect />
                  </FormControl>

                  <FormControl
                    title={t("forms.sector")}
                    type="custom"
                    isSelect
                    name="sector"
                    inputContainerStyle={styles.inputContainerStyle}
                    onChange={(val) => {
                      onChangeFilter("sector", val);
                    }}
                  >
                    <SectorSelect />
                  </FormControl>

                  <FormControl
                    title={t("forms.tag")}
                    type="custom"
                    isSelect
                    name="tag"
                    inputContainerStyle={styles.inputContainerStyle}
                    onChange={(val) => {
                      onChangeFilter("tag", val);
                    }}
                  >
                    <TagSelect />
                  </FormControl>
                  <FormControl
                    title={t("common.observer")}
                    type="custom"
                    isSelect
                    name="user"
                    inputContainerStyle={styles.inputContainerStyle}
                    onChange={(val) => {
                      onChangeFilter("user", val);
                    }}
                  >
                    <UserSelect />
                  </FormControl>
                  <FormControl
                    title={t("common.manager")}
                    type="custom"
                    isSelect
                    name="manager"
                    inputContainerStyle={styles.inputContainerStyle}
                    onChange={(val) => {
                      onChangeFilter("manager", val);
                    }}
                  >
                    <ManagerSelect />
                  </FormControl>
                  <FormControl
                    title={t("forms.superfamily")}
                    type="custom"
                    isSelect
                    name="superfamily"
                    inputContainerStyle={styles.inputContainerStyle}
                    onChange={(val) => {
                      onChangeFilter("superfamily", val);
                    }}
                  >
                    <SuperFamilySelect />
                  </FormControl>
                </>
              );
            }}
          />
        </div>
        <div className="flex flex-col gap-4">
          <h3 className="text-lg">{t("forms.graphVisibility")}</h3>
          <BalanceSheetDisplayToggles
            value={displayOptions}
            onChange={setDisplayOptions}
          />
        </div>
      </div>

      <BalanceSheetReport
        balance={balance}
        displayOptions={displayOptions}
        chartRef={chartRef}
      />

      <SaveBalanceSheetModal
        visible={isSaveModalVisible}
        isSaving={isSaving}
        startDate={filters.startDate}
        endDate={filters.endDate}
        onCancel={() => setIsSaveModalVisible(false)}
        onConfirm={handleSave}
      />
    </div>
  );
}

export default BalanceSheets;
